import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ApiService } from './api.service';

/**
 * Sitemap Service
 *
 * @export
 * @class Global
 */
@Injectable({ providedIn: 'root' })
export class SitemapService {
    private operatingStates$?: Observable<{ name: string; code: string }[]>;

    constructor(private apiService: ApiService) {}

    /**
     * Get cities
     * @returns
     */
    getSitemapCities() {
        return this.apiService.get('api/v1/sitemap/cities/count');
    }

    /**
     * Get communities for city
     * @param city
     * @returns
     */
    getCommunitiesByCity(state: string, city: string) {
        return this.apiService.get(`api/v1/sitemap/cities/${state}/${city}/communities/count`);
    }

    /**
     * Get sitemap blogs
     * @param params
     * @returns
     */
    getSitemapBlogs(params: {}) {
        return this.apiService.get(`api/v1/sitemap/blogs`, params);
    }

    /**
     * Get sitemap pages
     * @param params
     * @returns
     */
    getSitemapPages(params: {}) {
        return this.apiService.get(`api/v1/sitemap/pages`, params);
    }

    /**
     * Get sitemap listings
     * @param params
     * @returns
     */
    getSitemapListings(params: {}) {
        return this.apiService.get(`api/v1/sitemap/listings/paginated`, params);
    }

    /**
     * Returns the list of operating states for the company, cached for the lifetime of the service.
     * @returns Observable of state objects with name and code, shared and replayed.
     */
    getOperatingStates(): Observable<{ name: string; code: string }[]> {
        if (!this.operatingStates$) {
            this.operatingStates$ = this.apiService.get('api/v1/autosearch-items/states').pipe(
                map((response: any) => response.body?.data?.states || []),
                shareReplay(1),
            );
        }
        return this.operatingStates$;
    }

    /**
     * Fetches counties for the browse hierarchy, optionally scoped to a state.
     * @param params - Optional state code to filter counties.
     * @returns Observable of the API response containing counties.
     */
    getBrowseCounties(params?: { state?: string }) {
        const base = params?.state
            ? `api/v1/sitemap/browse/${params.state}/counties`
            : 'api/v1/sitemap/browse/counties';
        return this.apiService.get(base);
    }

    /**
     * Fetches zip codes for a county in the browse hierarchy.
     * @param county - County slug.
     * @param params - State code (required).
     * @returns Observable of the API response containing zips.
     */
    getBrowseZips(county: string, params: { state: string }) {
        return this.apiService.get(`api/v1/sitemap/browse/${params.state}/${county}/zips`);
    }

    /**
     * Fetches streets within a zip code for the browse hierarchy.
     * @param county - County slug.
     * @param zip - Zip code slug.
     * @param params - State code (required).
     * @returns Observable of the API response containing streets.
     */
    getBrowseStreets(county: string, zip: string, params: { state: string }) {
        return this.apiService.get(`api/v1/sitemap/browse/${params.state}/${county}/zips/${zip}/streets`);
    }

    /**
     * Fetches active property listings on a specific street for the browse hierarchy.
     * @param county - County slug.
     * @param zip - Zip code slug.
     * @param street - Street slug.
     * @param params - State code (required) and optional street_main query filter.
     * @returns Observable of the API response containing properties.
     */
    getBrowseProperties(county: string, zip: string, street: string, params: { state: string }) {
        return this.apiService.get(
            `api/v1/sitemap/browse/${params.state}/${county}/${zip}/${street}/properties`,
        );
    }

    /**
     * Fetches properties listed within the last 14 days for a county, sorted newest first.
     * @param county - County slug.
     * @param state - State code.
     * @returns Observable of the API response containing newest properties.
     */
    getBrowseNewestHomes(county: string, state: string) {
        return this.apiService.get(`api/v1/sitemap/browse/${state}/${county}/newest-homes`);
    }
}
